import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DataTable } from "@/components/DataTable";
import { Clock, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function TipiOre() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  const load = () => api.get("/hour-types").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setName(""); setIsDefault(false); setOpen(true); };
  const openEdit = (h) => { setEditing(h); setName(h.name); setIsDefault(!!h.is_default); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/hour-types/${editing.id}`, { name, is_default: isDefault });
      else await api.post("/hour-types", { name, is_default: isDefault });
      toast.success(editing ? "Tipo ore aggiornato" : "Tipo ore creato");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/hour-types/${id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error("Errore"); } };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Tipi di ore</h1>
          <p className="mt-1 text-sm text-slate-500">Tipologie di ore (es. Ordinarie, Straordinarie, Notturne). Ogni dipendente ha un costo orario per tipologia.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]" data-testid="add-hourtype-btn">
              <Plus className="mr-2 h-4 w-4" /> Nuovo tipo
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="hourtype-dialog">
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifica tipo ore" : "Nuovo tipo ore"}</DialogTitle></DialogHeader>
            <div><Label htmlFor="ht-name">Nome</Label><Input id="ht-name" data-testid="hourtype-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder="es. Ordinarie" /></div>
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700" data-testid="hourtype-default-toggle">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]" />
              Predefinito (usato quando il rapportino non ha un tipo di ore selezionato, es. da check-in)
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={save} disabled={!name} data-testid="save-hourtype-btn" className="bg-[#002FA7] text-white hover:bg-[#00227A]">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="rounded-lg border-slate-200 shadow-sm">
        <div className="p-4">
          <DataTable data={items} testid="hourtypes-table" rowTestId={(h) => `hourtype-row-${h.id}`}
            searchKeys={["name"]} searchPlaceholder="Cerca tipo..." initialSort={{ key: "name", dir: "asc" }}
            emptyIcon={Clock} emptyText="Nessun tipo di ore"
            columns={[
              { key: "name", header: "Nome", sortable: true, className: "font-medium text-slate-900", cell: (h) => <span className="flex items-center gap-2">{h.name}{h.is_default && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-[#002FA7]" data-testid={`hourtype-default-badge-${h.id}`}>Predefinito</span>}</span> },
              { key: "actions", header: "Azioni", align: "right", cell: (h) => (
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(h)} data-testid={`edit-hourtype-${h.id}`} className="rounded-md"><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(h.id)} data-testid={`delete-hourtype-${h.id}`} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ) },
            ]} />
        </div>
      </Card>
    </div>
  );
}
